// Used during the onboarding flow allowing the user to choose their plan
import { ConfirmDialog } from "@/components/ConfirmDialog";
import Loading from "@/components/Loading";
import { useAuthContext } from "@/contexts/AuthContext";
import useCurrentUser from "@/hooks/useCurrentUser";
import useSubscriptionPermission from "@/hooks/useSubscriptionPermission";
import { useCroWeb168Experiment } from "@/src/experiments/cro-web-168/useCroWeb168Experiment";
import ElemseeTestimonial from "@/src/features/onboarding/components/ElemseeTestimonial";
import FeatureList from "@/src/features/onboarding/components/FeatureList";
import { PlanSelection } from "@/src/features/onboarding/components/PlanSelection";
import useSessionCouponCode from "@/src/hooks/useSessionCouponCode";
import clsx from "clsx";
import { observer } from "mobx-react";
import { useRouter } from "next/router";
import { useEffect, useRef, useState } from "react";
import SubscriptionCards from "./SubscriptionCards";

export const ChooseYourPlan = observer(() => {
  const { couponCode, isLoading } = useSessionCouponCode();
  const { shouldShowSubscriptionCards } = useCroWeb168Experiment();
  const router = useRouter();
  const [smallModalOpen, setSmallModalOpen] = useState(false);
  const { hasHadATrial } = useSubscriptionPermission();
  const {
    displayCurrency,
    logOnboardingStep,
    currentStepIndex,
    otherObjective,
    otherText,
    otherCategory,
  } = useAuthContext();
  const routerRef = useRef(router);

  const { currentUser } = useCurrentUser();

  useEffect(() => {
    if (currentUser && !currentUser.hasCompletedOnboarding) {
      currentUser.setHasCompletedOnboarding(true);
      currentUser.save();
    }
  }, [currentUser]);

  const handleSmallPopupClose = () => setSmallModalOpen(false);

  const handleClose = () => {
    setSmallModalOpen(false);
    routerRef.current.push("/", "/", { shallow: true });
  };

  return (
    <>
      {isLoading && <Loading />}

      <div className="flex h-full w-full flex-col gap-6">
        <h1 className={clsx("heading-md--spatial")}>
          Get the full Mob experience today
        </h1>
        {shouldShowSubscriptionCards ? (
          <SubscriptionCards
            hasHadATrial={hasHadATrial}
            couponCode={couponCode}
            displayCurrency={displayCurrency}
            currentStepIndex={currentStepIndex}
            logOnboardingStep={logOnboardingStep}
            otherObjective={otherObjective}
            otherText={otherText}
            otherCategory={otherCategory}
          />
        ) : (
          <>
            <FeatureList />
            <ElemseeTestimonial />
            <PlanSelection
              hasHadATrial={hasHadATrial}
              couponCode={couponCode}
              currentStepIndex={currentStepIndex}
              logOnboardingStep={logOnboardingStep}
              otherObjective={otherObjective}
              otherText={otherText}
              otherCategory={otherCategory}
            />
          </>
        )}
      </div>

      <ConfirmDialog
        open={smallModalOpen}
        onOpenChange={setSmallModalOpen}
        title="Are you sure?"
        description="Sign up for a Premium account to get unlimited access to our recipes, meal plans and so much more."
        confirmLabel="Choose Plan"
        cancelLabel="Close"
        onConfirm={handleSmallPopupClose}
        onCancel={handleClose}
      />
    </>
  );
});
